import { useState, useEffect, useCallback, useMemo } from "react";
import FirebaseBiodataRepository from '../api/FirebaseBiodataRepository';
import GetBiodataProfilesUseCase from '../use-cases/GetBiodataProfilesUseCase';
import DeleteBiodataUseCase from '../use-cases/DeleteBiodataUseCase';
import UpdateBiodataStatusUseCase from '../use-cases/UpdateBiodataStatusUseCase';

const useBiodataViewModel = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Instantiate repositories and use cases once using useMemo
  const biodataRepository = useMemo(() => new FirebaseBiodataRepository(), []);
  const getBiodataProfilesUseCase = useMemo(() => new GetBiodataProfilesUseCase(biodataRepository), [biodataRepository]);
  const deleteBiodataUseCase = useMemo(() => new DeleteBiodataUseCase(biodataRepository), [biodataRepository]);
  const updateBiodataStatusUseCase = useMemo(() => new UpdateBiodataStatusUseCase(biodataRepository), [biodataRepository]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const profilesList = await getBiodataProfilesUseCase.execute();
      setProfiles(profilesList);
    } catch (err) {
      console.error("Error fetching profiles:", err);
      const errorMessage = err.code === 'permission-denied' 
        ? "Permission denied. Please check Firebase security rules."
        : err.code === 'failed-precondition'
        ? "Database index missing. Please create an index for 'createdAt' field."
        : `Failed to fetch profiles: ${err.message || 'Unknown error'}`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [getBiodataProfilesUseCase]);

  const handleDelete = useCallback(async (id) => {
    setError(null);
    try {
      await deleteBiodataUseCase.execute(id);
      setProfiles((prevProfiles) => prevProfiles.filter((profile) => profile.id !== id));
      return true; // Indicate success
    } catch (err) {
      console.error("Error deleting profile:", err);
      setError("Failed to delete profile.");
      return false; // Indicate failure
    }
  }, [deleteBiodataUseCase]);

  const handleStatusChange = useCallback(async (id, newStatus) => {
    setError(null);
    try {
      await updateBiodataStatusUseCase.execute(id, newStatus);
      setProfiles((prevProfiles) =>
        prevProfiles.map((profile) =>
          profile.id === id ? { ...profile, status: newStatus } : profile
        )
      );
      return true; // Indicate success
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update profile status.");
      return false; // Indicate failure
    }
  }, [updateBiodataStatusUseCase]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return {
    profiles,
    loading,
    error,
    fetchProfiles,
    handleDelete,
    handleStatusChange,
  };
};

export default useBiodataViewModel;
